(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const roots = [...document.querySelectorAll('[data-al-home], [data-al-product], [data-al-cart]')];

  const cleanTitle = (title = '') =>
    title.replace(/^aldren signature\s*/i, '').replace(/\s*t700$/i, '').trim();

  roots.forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.alInitialized === 'true') return;
    root.dataset.alInitialized = 'true';

    const header = root.querySelector('[data-al-header]');
    const menuButton = root.querySelector('[data-al-menu-button]');
    const cartStatus = root.querySelector('[data-al-cart-status]');
    const scrollProgress = root.querySelector('[data-al-scroll-progress]');
    const isProductPage = root.hasAttribute('data-al-product');
    const isCartPage = root.hasAttribute('data-al-cart');
    const scrollHost = window;
    const pageWrapper = document.querySelector('.page-wrapper');
    const scrollTargets = [...new Set([window, document, document.documentElement, document.body, pageWrapper].filter(Boolean))];
    const getScrollY = () =>
      Math.max(
        Number(window.scrollY) || 0,
        Number(window.pageYOffset) || 0,
        Number(document.documentElement?.scrollTop) || 0,
        Number(document.body?.scrollTop) || 0,
        pageWrapper instanceof HTMLElement ? Number(pageWrapper.scrollTop) || 0 : 0
      );
    const getScrollMax = () =>
      Math.max(
        1,
        (document.documentElement?.scrollHeight || 0) - window.innerHeight,
        (document.body?.scrollHeight || 0) - window.innerHeight,
        pageWrapper instanceof HTMLElement ? pageWrapper.scrollHeight - pageWrapper.clientHeight : 0
      );
    const listenToScroll = (callback) => {
      scrollTargets.forEach((target) => target.addEventListener('scroll', callback, { passive: true }));
    };
    let statusTimer = 0;
    const stockInLabel = root.dataset.alStockInLabel || 'In stock and ready to ship.';
    const stockSoldOutLabel = root.dataset.alStockSoldOutLabel || 'Currently sold out.';
    const stockLowPrefix = root.dataset.alStockLowPrefix || 'Only';
    const stockLowSuffix = root.dataset.alStockLowSuffix || 'left in this finish.';
    const addToBagLabel = root.dataset.alAddToBagLabel || 'Add to bag';
    const soldOutLabel = root.dataset.alSoldOutLabel || 'Sold out';
    const addingLabel = root.dataset.alAddingLabel || 'Adding…';

    /* Cinematic page entrance. The content stays usable if motion is reduced or JS arrives late. */
    const motionLoader = root.querySelector('[data-al-motion-loader]');
    const motionDisabled = reduceMotion.matches || Boolean(window.Shopify?.designMode);
    root.classList.add('al-motion-enabled');

    if (motionDisabled) {
      root.classList.add('is-motion-ready', 'is-motion-settled');
      motionLoader?.remove();
    } else {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => root.classList.add('is-motion-ready'));
      });
      window.setTimeout(() => {
        root.classList.add('is-motion-settled');
        motionLoader?.remove();
      }, 1950);
    }

    const liquidSections = [
      ...root.querySelectorAll(
        '.al-featured, .al-catalogue, .al-platform, .al-material, .al-quiz, .al-sets, .al-heritage, .al-proof, .al-pdp-manifesto, .al-pdp-editorial, .al-pdp-specs, .al-pdp-comparison, .al-pdp-faq, .al-pdp-more, .al-cart-hero, .al-cart-items, .al-cart-summary, .al-cart-promise'
      )
    ];
    liquidSections.forEach((section) => section.setAttribute('data-al-liquid-section', ''));

    const showStatus = (message, isError = false) => {
      if (!(cartStatus instanceof HTMLElement)) return;
      window.clearTimeout(statusTimer);
      cartStatus.textContent = message;
      cartStatus.style.background = isError ? 'var(--al-oxblood)' : 'var(--al-navy)';
      cartStatus.classList.add('is-visible');
      statusTimer = window.setTimeout(() => cartStatus.classList.remove('is-visible'), 4200);
    };

    let lastHeaderY = getScrollY();

    const updateHeader = () => {
      if (!(header instanceof HTMLElement)) return;
      const currentY = getScrollY();
      const menuOpen = header.classList.contains('is-menu-open');
      const atTop = currentY <= 18;
      const movingDown = currentY > lastHeaderY + 6;
      const movingUp = currentY < lastHeaderY - 6;

      header.classList.toggle('is-solid', isProductPage || isCartPage || currentY > 48 || menuOpen);
      header.classList.toggle('is-at-top', atTop);

      header.classList.toggle('is-hidden', !atTop && currentY > 88 && !menuOpen);
      lastHeaderY = currentY;

      if (scrollProgress instanceof HTMLElement) {
        const maxScroll = getScrollMax();
        scrollProgress.style.transform = `scaleX(${Math.max(0, Math.min(1, currentY / maxScroll))})`;
      }
    };

    updateHeader();
    listenToScroll(updateHeader);

    menuButton?.addEventListener('click', () => {
      const open = !header?.classList.contains('is-menu-open');
      header?.classList.toggle('is-menu-open', open);
      header?.classList.remove('is-hidden');
      menuButton.setAttribute('aria-expanded', String(open));
      menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      updateHeader();
    });

    root.querySelectorAll('.al-mobile-nav a').forEach((link) => {
      link.addEventListener('click', () => {
        header?.classList.remove('is-menu-open');
        menuButton?.setAttribute('aria-expanded', 'false');
        menuButton?.setAttribute('aria-label', 'Open menu');
        updateHeader();
      });
    });

    const reveals = [...root.querySelectorAll('.al-reveal')];
    if (reduceMotion.matches || window.Shopify?.designMode) {
      reveals.forEach((item) => item.setAttribute('data-al-revealed', 'true'));
    } else if ('IntersectionObserver' in window) {
      const revealTargets = new Map();
      reveals.forEach((item) => {
        const target = item.classList.contains('al-reveal-mask') ? item.parentElement || item : item;
        const grouped = revealTargets.get(target) || [];
        grouped.push(item);
        revealTargets.set(target, grouped);
      });
      const revealGroup = (items) => {
        const entranceDelay = root.classList.contains('is-motion-settled') ? 0 : 720;
        window.setTimeout(() => {
          items.forEach((item) => item.setAttribute('data-al-revealed', 'true'));
        }, entranceDelay);
      };
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealGroup(revealTargets.get(entry.target) || []);
            revealObserver.unobserve(entry.target);
          });
        },
        { root: scrollHost instanceof HTMLElement ? scrollHost : null, rootMargin: '0px 0px -11% 0px', threshold: 0.05 }
      );
      revealTargets.forEach((_items, target) => revealObserver.observe(target));
    } else {
      let pendingReveals = reveals;
      let revealFrame = 0;
      const revealVisible = () => {
        revealFrame = 0;
        const revealLine = window.innerHeight * 0.94;
        pendingReveals = pendingReveals.filter((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.top < revealLine && rect.bottom > 0) {
            const entranceDelay = root.classList.contains('is-motion-settled') ? 0 : 720;
            window.setTimeout(() => item.setAttribute('data-al-revealed', 'true'), entranceDelay);
            return false;
          }
          return true;
        });
      };
      const requestReveal = () => {
        if (revealFrame || !pendingReveals.length) return;
        revealFrame = window.requestAnimationFrame(revealVisible);
      };
      listenToScroll(requestReveal);
      window.addEventListener('resize', requestReveal, { passive: true });
      window.requestAnimationFrame(revealVisible);
    }

    window.setTimeout(() => {
      reveals.forEach((item) => item.setAttribute('data-al-revealed', 'true'));
    }, 2800);

    const heroMedia = root.querySelector('[data-al-hero-media]');
    const parallaxMedia = [...root.querySelectorAll('[data-al-parallax-media]')];
    let parallaxFrame = 0;
    let lastMotionY = getScrollY();
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    const updateParallax = () => {
      parallaxFrame = 0;
      if (reduceMotion.matches) return;

      const currentY = getScrollY();
      const isCompactMotion = window.innerWidth < 990;
      const viewportHeight = Math.max(
        1,
        scrollHost instanceof HTMLElement ? scrollHost.clientHeight : window.innerHeight
      );
      const scrollEnergy = clamp(Math.abs(currentY - lastMotionY) / 52, 0, 1);
      root.style.setProperty('--al-scroll-energy', scrollEnergy.toFixed(3));
      lastMotionY = currentY;

      if (heroMedia instanceof HTMLElement) {
        const image = heroMedia.querySelector('img');
        if (image instanceof HTMLElement) {
          const heroProgress = clamp(currentY / (viewportHeight * 0.92), 0, 1);
          const offset = heroProgress * 92;
          image.style.setProperty('--al-parallax-y', `${offset}px`);
          heroMedia.style.setProperty('--al-hero-progress', heroProgress.toFixed(4));
        }
      }

      parallaxMedia.forEach((frame, index) => {
        if (!(frame instanceof HTMLElement)) return;
        const rect = frame.getBoundingClientRect();
        if (rect.bottom < -180 || rect.top > viewportHeight + 180) {
          frame.classList.remove('is-al-motion-active');
          return;
        }
        frame.classList.add('is-al-motion-active');
        const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height), 0, 1);
        const centered = progress - 0.5;
        const arc = Math.sin(progress * Math.PI);
        const direction = index % 2 === 0 ? -1 : 1;
        frame.style.setProperty('--al-media-y', `${centered * (isCompactMotion ? -30 : -72)}px`);
        frame.style.setProperty('--al-liquid-x', `${arc * direction * (isCompactMotion ? 3.5 : 8)}px`);
        frame.style.setProperty('--al-liquid-rotate', `${centered * direction * (isCompactMotion ? 0.26 : 0.7)}deg`);
        frame.style.setProperty('--al-liquid-radius', `${arc * (isCompactMotion ? 8 : 14)}px`);
        frame.style.setProperty('--al-media-progress', progress.toFixed(4));
      });

      liquidSections.forEach((section) => {
        if (!(section instanceof HTMLElement)) return;
        const rect = section.getBoundingClientRect();
        if (rect.bottom < -180 || rect.top > viewportHeight + 180) return;
        const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height), 0, 1);
        section.style.setProperty('--al-section-progress', progress.toFixed(4));
        section.style.setProperty('--al-title-drift', `${(0.5 - progress) * (isCompactMotion ? 14 : 34)}px`);
        section.style.setProperty('--al-section-radius', `${Math.sin(progress * Math.PI) * (isCompactMotion ? 12 : 22)}px`);
      });
    };

    const requestParallax = () => {
      if (parallaxFrame) return;
      parallaxFrame = window.requestAnimationFrame(updateParallax);
    };

    updateParallax();
    listenToScroll(requestParallax);
    window.addEventListener('resize', requestParallax, { passive: true });

    const refreshCartCount = async () => {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be refreshed.');
      const cart = await response.json();
      root.querySelectorAll('[data-al-cart-count]').forEach((node) => {
        node.textContent = String(cart.item_count || 0);
        node.setAttribute('data-empty', String(!cart.item_count));
      });
      const cartLink = root.querySelector('[data-al-cart-link]');
      cartLink?.setAttribute('aria-label', `Cart, ${cart.item_count || 0} items`);
      return cart;
    };

    const addItems = async (items) => {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${routeRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || 'These colourways could not be added.');
      const cart = await refreshCartCount();
      return cart;
    };

    const addVariant = async (id, quantity = 1) => addItems([{ id, quantity }]);

    root.querySelectorAll('[data-al-quick-add-form]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const id = new FormData(form).get('id');
        const original = button?.textContent || 'Add to bag';
        if (!id) return;

        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
          button.textContent = addingLabel;
        }

        try {
          const cart = await addVariant(String(id));
          if (button) button.textContent = 'Added';
          showStatus(`Added to your bag. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart.`);
        } catch (error) {
          if (button) button.textContent = original;
          if (button instanceof HTMLButtonElement) button.disabled = false;
          showStatus(error instanceof Error ? error.message : 'Could not add this colourway.', true);
          return;
        }

        window.setTimeout(() => {
          if (button) button.textContent = original;
          if (button instanceof HTMLButtonElement) button.disabled = false;
        }, 1800);
      });
    });

    /* Shopify-native product page: live variants, image handoff, quantity and cart. */
    if (isProductPage) {
      const variantsNode = root.querySelector('[data-al-product-variants]');
      const productForm = root.querySelector('.al-pdp-form');
      const stickyBar = root.querySelector('[data-al-product-sticky]');
      let productVariants = [];
      const bundleBuilder = root.querySelector('[data-al-bundle-builder]');
      const bundleSlotsContainer = root.querySelector('[data-al-bundle-slots]');
      const bundleSlots = Array.from(root.querySelectorAll('[data-al-bundle-slot]'));
      const bundleSelects = Array.from(root.querySelectorAll('[data-al-bundle-variant]'));
      const bundleSummary = root.querySelector('[data-al-bundle-summary]');
      const bundleEmptyLabel = bundleSummary?.textContent?.trim() || 'Select a set above to choose every colourway.';
      const bundleError = root.querySelector('[data-al-bundle-error]');
      const bundleAddLabel = root.getAttribute('data-al-bundle-add-label') || 'Add selected set to bag';
      const bundleErrorLabel = root.getAttribute('data-al-bundle-error-label') || 'Choose an available colourway for every paddle.';
      const bundleCollectionName = root.getAttribute('data-al-bundle-collection-name') || 'Aldren';
      let activeBundleQuantity = 0;
      let syncBundleBuilder = () => {};

      try {
        productVariants = JSON.parse(variantsNode?.textContent || '[]');
      } catch {
        productVariants = [];
      }

      const setProductVariant = (variant, updateUrl = true) => {
        if (!variant) return;

        const mediaScale = Number.parseFloat(variant.mediaScale);
        root.style.setProperty('--al-pdp-variant-scale', Number.isFinite(mediaScale) ? String(mediaScale) : '1');

        root.querySelectorAll('[data-al-selected-name]').forEach((node) => {
          node.textContent = variant.name;
          if (!motionDisabled && node instanceof HTMLElement && typeof node.animate === 'function') {
            node.animate(
              [
                { opacity: 0.18, transform: 'translate3d(0, 18px, 0) skewY(3deg)', filter: 'blur(5px)' },
                { opacity: 1, transform: 'translate3d(0, 0, 0) skewY(0deg)', filter: 'blur(0)' }
              ],
              { duration: 760, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' }
            );
          }
        });
        root.querySelectorAll('[data-al-product-variant-input]').forEach((input) => {
          if (input instanceof HTMLInputElement) input.value = String(variant.id);
        });
        root.querySelectorAll('[data-al-product-price]').forEach((node) => {
          node.textContent = variant.price;
        });
        root.querySelectorAll('[data-al-product-sticky-price]').forEach((node) => {
          node.textContent = variant.price;
        });
        root.querySelectorAll('[data-al-product-sku]').forEach((node) => {
          node.textContent = variant.sku || 'Not assigned';
        });

        root.querySelectorAll('[data-al-product-compare]').forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          node.hidden = !variant.compareAt;
          node.textContent = variant.compareAt || '';
        });

        root.querySelectorAll('[data-al-product-stock]').forEach((node) => {
          node.textContent = !variant.available
            ? stockSoldOutLabel
            : variant.lowStock
              ? `${stockLowPrefix} ${variant.inventory} ${stockLowSuffix}`
              : stockInLabel;
        });

        root.querySelectorAll('[data-al-product-submit], [data-al-sticky-submit]').forEach((button) => {
          if (!(button instanceof HTMLButtonElement)) return;
          button.disabled = !variant.available;
          button.textContent = variant.available ? addToBagLabel : soldOutLabel;
        });

        root.querySelectorAll('[data-al-variant-option]').forEach((button) => {
          const selected = button.getAttribute('data-variant-id') === String(variant.id);
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-checked', String(selected));
        });

        const colourCopy = root.querySelector('[data-al-colourway-description]');
        if (colourCopy instanceof HTMLElement) {
          colourCopy.hidden = !variant.description;
          colourCopy.textContent = variant.description || '';
        }

        const seenGalleryImages = new Set();
        const gallery = Array.isArray(variant.gallery)
          ? variant.gallery.flatMap((item, fallbackIndex) => {
              if (!item?.image || seenGalleryImages.has(item.image)) return [];
              seenGalleryImages.add(item.image);
              const parsedIndex = Number(item.index);
              return [{
                ...item,
                index: Number.isInteger(parsedIndex) ? parsedIndex : fallbackIndex
              }];
            })
          : [];
        const galleryIndexes = new Set(gallery.map((media) => String(media.index)));

        root.querySelectorAll('[data-al-product-gallery-frame]').forEach((frame) => {
          if (!(frame instanceof HTMLElement)) return;
          frame.hidden = !galleryIndexes.has(String(frame.getAttribute('data-al-product-gallery-frame')));
        });

        if (gallery.length) {
          const mediaChanges = gallery.map((media) => ({
            selector: `[data-al-product-gallery-image="${media.index}"]`,
            src: media.image,
            alt: media.alt || `ALDREN ${variant.name}`
          }));

          Promise.all(
            [...new Set(mediaChanges.map((change) => change.src))].map(
              (src) =>
                new Promise((resolve) => {
                  const preload = new Image();
                  preload.onload = resolve;
                  preload.onerror = resolve;
                  preload.src = src;
                })
            )
          ).then(() => {
            root.querySelectorAll('[data-al-product-stage]').forEach((stage) => stage.classList.add('is-variant-changing'));
            mediaChanges.forEach((change) => {
              root.querySelectorAll(change.selector).forEach((image) => {
                if (!(image instanceof HTMLImageElement)) return;
                image.classList.add('is-switching');
                window.setTimeout(() => {
                  image.src = change.src;
                  image.removeAttribute('srcset');
                  image.alt = change.alt;
                  const frame = image.closest('[data-al-product-gallery-frame]');
                  if (frame instanceof HTMLElement) frame.hidden = false;
                  window.requestAnimationFrame(() => window.requestAnimationFrame(() => image.classList.remove('is-switching')));
                }, 220);
              });
            });
            window.setTimeout(() => {
              root.querySelectorAll('[data-al-product-stage]').forEach((stage) => stage.classList.remove('is-variant-changing'));
            }, 980);
          });
        }

        const preferredMasterId = String(variant.masterId || variant.id);
        const selectsToSync = activeBundleQuantity > 0 ? bundleSelects.slice(0, 1) : bundleSelects;
        selectsToSync.forEach((select) => {
          if (!(select instanceof HTMLSelectElement)) return;
          const preferred = Array.from(select.options).find(
            (option) => option.dataset.masterVariantId === preferredMasterId && !option.disabled
          );
          if (preferred) select.value = preferred.value;
        });

        if (updateUrl && variant.url && window.history?.replaceState) {
          window.history.replaceState({ aldrenVariant: variant.id }, '', variant.url);
        }
        syncBundleBuilder();
      };

      root.querySelectorAll('[data-al-variant-option]').forEach((button) => {
        button.addEventListener('click', () => {
          const variant = productVariants.find(
            (item) => String(item.masterId || item.id) === button.getAttribute('data-variant-id')
          );
          setProductVariant(variant);
          if (!motionDisabled && button instanceof HTMLElement && typeof button.animate === 'function') {
            button.animate(
              [
                { transform: 'translateY(0) scale(1)' },
                { transform: 'translateY(-5px) scale(0.97)' },
                { transform: 'translateY(0) scale(1)' }
              ],
              { duration: 640, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
            );
          }
        });
      });

      const quantity = root.querySelector('[data-al-quantity]');
      const updateQuantity = (delta) => {
        if (!(quantity instanceof HTMLInputElement)) return;
        const current = Number.parseInt(quantity.value || '1', 10) || 1;
        quantity.value = String(Math.max(1, Math.min(10, current + delta)));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      };
      root.querySelector('[data-al-qty-minus]')?.addEventListener('click', () => updateQuantity(-1));
      root.querySelector('[data-al-qty-plus]')?.addEventListener('click', () => updateQuantity(1));

      const quantityPresets = Array.from(root.querySelectorAll('[data-al-quantity-preset]'));
      const currentProductVariant = () => {
        const id = root.querySelector('[data-al-product-variant-input]')?.value;
        return productVariants.find((variant) => String(variant.id) === String(id));
      };
      const visibleBundleSelects = () =>
        bundleSlots
          .slice(0, activeBundleQuantity)
          .map((slot) => slot.querySelector('[data-al-bundle-variant]'))
          .filter((select) => select instanceof HTMLSelectElement);

      syncBundleBuilder = () => {
        const requested = quantity instanceof HTMLInputElement
          ? Number.parseInt(quantity.value || '1', 10) || 1
          : 1;
        activeBundleQuantity = [2, 4].includes(requested) && bundleBuilder ? requested : 0;

        bundleSlots.forEach((slot, index) => {
          const visible = index < activeBundleQuantity;
          slot.hidden = !visible;
          const select = slot.querySelector('[data-al-bundle-variant]');
          if (!(select instanceof HTMLSelectElement)) return;
          select.disabled = !visible;
          select.required = visible;
          if (visible && (!select.value || select.selectedOptions[0]?.disabled)) {
            const fallback = Array.from(select.options).find((option) => !option.disabled);
            if (fallback) select.value = fallback.value;
          }
        });

        if (bundleBuilder instanceof HTMLElement) bundleBuilder.classList.toggle('is-active', activeBundleQuantity > 0);
        if (bundleSlotsContainer instanceof HTMLElement) bundleSlotsContainer.hidden = activeBundleQuantity === 0;
        if (bundleError instanceof HTMLElement) bundleError.hidden = true;

        const activeSelects = visibleBundleSelects();
        const selectionsValid =
          activeSelects.length === activeBundleQuantity &&
          activeSelects.every((select) => select.value && !select.selectedOptions[0]?.disabled);
        if (bundleSummary instanceof HTMLElement) {
          bundleSummary.textContent = activeBundleQuantity > 0
            ? activeSelects.map((select) => select.selectedOptions[0]?.textContent?.trim() || '').join(' · ')
            : bundleEmptyLabel;
        }

        const activeVariant = currentProductVariant();
        root.querySelectorAll('[data-al-product-submit], [data-al-sticky-submit]').forEach((button) => {
          if (!(button instanceof HTMLButtonElement)) return;
          if (activeBundleQuantity > 0) {
            button.disabled = !selectionsValid;
            button.textContent = bundleAddLabel;
          } else {
            button.disabled = !activeVariant?.available;
            button.textContent = activeVariant?.available ? addToBagLabel : soldOutLabel;
          }
        });
      };

      const syncQuantityPreset = () => {
        if (!(quantity instanceof HTMLInputElement)) return;
        quantityPresets.forEach((button) => {
          const isSelected = button.getAttribute('data-al-quantity-preset') === quantity.value;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
        syncBundleBuilder();
      };

      quantityPresets.forEach((button) => {
        button.addEventListener('click', () => {
          if (!(quantity instanceof HTMLInputElement)) return;
          const preset = Number.parseInt(button.getAttribute('data-al-quantity-preset') || '', 10);
          if (!Number.isFinite(preset)) return;
          quantity.value = String(preset);
          quantity.dispatchEvent(new Event('input', { bubbles: true }));
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      bundleSelects.forEach((select) => select.addEventListener('change', syncBundleBuilder));
      quantity?.addEventListener('input', syncQuantityPreset);
      quantity?.addEventListener('change', syncQuantityPreset);
      syncQuantityPreset();

      if (productForm instanceof HTMLFormElement) {
        productForm.addEventListener('submit', async (event) => {
          event.preventDefault();
          const formData = new FormData(productForm);
          const id = formData.get('id');
          const requestedQuantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1);
          const submit = root.querySelector('[data-al-product-submit]');
          if (!id || !(submit instanceof HTMLButtonElement)) return;

          const original = submit.textContent;
          submit.disabled = true;
          submit.textContent = addingLabel;
          try {
            let cart;
            if (activeBundleQuantity > 0) {
              const activeSelects = visibleBundleSelects();
              if (activeSelects.length !== activeBundleQuantity || activeSelects.some((select) => !select.value || select.selectedOptions[0]?.disabled)) {
                throw new Error(bundleErrorLabel);
              }
              const grouped = new Map();
              activeSelects.forEach((select) => grouped.set(select.value, (grouped.get(select.value) || 0) + 1));
              const items = Array.from(grouped, ([variantId, itemQuantity]) => ({ id: variantId, quantity: itemQuantity }));
              cart = await addItems(items);
              submit.textContent = 'Set added';
              showStatus(`Your ${activeBundleQuantity}-paddle ${bundleCollectionName} set was added. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart.`);
            } else {
              cart = await addVariant(String(id), requestedQuantity);
              submit.textContent = 'Added';
              showStatus(`Added to your bag. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart.`);
            }
          } catch (error) {
            submit.textContent = original;
            submit.disabled = false;
            const message = error instanceof Error ? error.message : 'Could not add this paddle.';
            if (bundleError instanceof HTMLElement && activeBundleQuantity > 0) {
              bundleError.textContent = message;
              bundleError.hidden = false;
            }
            showStatus(message, true);
            return;
          }

          window.setTimeout(() => {
            submit.textContent = original;
            submit.disabled = false;
            syncBundleBuilder();
          }, 1800);
        });
      }
      root.querySelector('[data-al-sticky-submit]')?.addEventListener('click', () => {
        productForm?.requestSubmit();
      });

      const updateStickyBar = () => {
        stickyBar?.classList.toggle('is-visible', getScrollY() > 520);
      };
      updateStickyBar();
      listenToScroll(updateStickyBar);

      if (!reduceMotion.matches && window.matchMedia('(pointer: fine)').matches) {
        root.querySelectorAll('[data-al-product-stage]').forEach((stage) => {
          if (!(stage instanceof HTMLElement)) return;
          let targetX = 0;
          let targetY = 0;
          let currentX = 0;
          let currentY = 0;
          let tiltFrame = 0;

          const renderTilt = () => {
            currentX += (targetX - currentX) * 0.12;
            currentY += (targetY - currentY) * 0.12;
            stage.style.setProperty('--al-tilt-x', `${currentX}px`);
            stage.style.setProperty('--al-tilt-y', `${currentY}px`);
            stage.style.setProperty('--al-tilt-rx', `${currentY * -0.16}deg`);
            stage.style.setProperty('--al-tilt-ry', `${currentX * 0.18}deg`);

            if (Math.abs(targetX - currentX) > 0.03 || Math.abs(targetY - currentY) > 0.03) {
              tiltFrame = window.requestAnimationFrame(renderTilt);
            } else {
              tiltFrame = 0;
            }
          };

          const requestTilt = () => {
            if (!tiltFrame) tiltFrame = window.requestAnimationFrame(renderTilt);
          };

          stage.addEventListener('pointermove', (event) => {
            const rect = stage.getBoundingClientRect();
            targetX = ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 18;
            targetY = ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 14;
            requestTilt();
          });
          stage.addEventListener('pointerleave', () => {
            targetX = 0;
            targetY = 0;
            requestTilt();
          });
        });
      }
    }

    /* Cart controls keep Shopify's native form as the source of truth while making
       quantity changes tactile and obvious on touch screens. */
    if (isCartPage) {
      const cartForm = root.querySelector('#al-cart-form');
      const markCartDirty = () => {
        cartForm?.classList.add('is-dirty');
        const updateButton = cartForm?.querySelector('button[name="update"]');
        if (updateButton) updateButton.textContent = 'Apply changes';
      };

      const changeLineQuantity = (index, delta) => {
        const input = cartForm?.querySelector(`[data-al-cart-quantity][data-index="${index}"]`);
        if (!(input instanceof HTMLInputElement)) return;
        const current = Number.parseInt(input.value || '0', 10) || 0;
        input.value = String(Math.max(0, Math.min(10, current + delta)));
        input.dispatchEvent(new Event('change', { bubbles: true }));

        if (!motionDisabled && typeof input.animate === 'function') {
          input.animate(
            [
              { transform: 'translateY(0)', opacity: 1 },
              { transform: `translateY(${delta > 0 ? '-8px' : '8px'})`, opacity: 0.18 },
              { transform: 'translateY(0)', opacity: 1 }
            ],
            { duration: 440, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
          );
        }
      };

      root.querySelectorAll('[data-al-cart-minus]').forEach((button) => {
        button.addEventListener('click', () => changeLineQuantity(button.getAttribute('data-index'), -1));
      });
      root.querySelectorAll('[data-al-cart-plus]').forEach((button) => {
        button.addEventListener('click', () => changeLineQuantity(button.getAttribute('data-index'), 1));
      });
      root.querySelectorAll('[data-al-cart-quantity]').forEach((input) => {
        input.addEventListener('change', markCartDirty);
      });
    }

    /* Functional, intentionally transparent fitting logic. All current variants share one platform;
       the quiz selects only the visual finish using live availability. */
    const dialog = root.querySelector('[data-al-quiz-dialog]');
    const quizForm = root.querySelector('[data-al-quiz-form]');
    const steps = [...root.querySelectorAll('[data-al-quiz-step]')];
    const result = root.querySelector('[data-al-quiz-result]');
    const quizActions = root.querySelector('[data-al-quiz-actions]');
    const progress = root.querySelector('[data-al-quiz-progress]');
    const progressBar = root.querySelector('[data-al-quiz-bar]');
    const backButton = root.querySelector('[data-al-quiz-back]');
    const nextButton = root.querySelector('[data-al-quiz-next]');
    let activeStep = 0;
    let selectedVariant = null;
    let variants = [];

    try {
      variants = JSON.parse(root.querySelector('[data-al-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }

    const setStep = (index) => {
      activeStep = Math.max(0, Math.min(steps.length - 1, index));
      steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === activeStep));
      if (backButton instanceof HTMLButtonElement) backButton.disabled = activeStep === 0;
      if (nextButton) nextButton.textContent = activeStep === steps.length - 1 ? 'See my match' : 'Next';
      if (progress) progress.textContent = `Question ${activeStep + 1} of ${steps.length}`;
      if (progressBar instanceof HTMLElement) progressBar.style.width = `${((activeStep + 1) / steps.length) * 100}%`;
      steps[activeStep]?.querySelector('input')?.focus({ preventScroll: true });
    };

    const scoreVariant = (variant, answers) => {
      const title = cleanTitle(variant.title).toLowerCase();
      let score = 0;
      const groups = {
        dark: ['onyx', 'vanguard', 'black', 'navy', 'midnight'],
        warm: ['verona', 'bordeaux', 'heritage', 'ascot', 'gold', 'brown', 'wine'],
        light: ['ivory', 'white', 'silver', 'cream', 'pearl']
      };
      if (answers.colour !== 'open' && groups[answers.colour]?.some((word) => title.includes(word))) score += 7;
      if (answers.character === 'understated' && groups.dark.some((word) => title.includes(word))) score += 4;
      if (answers.character === 'classic' && groups.warm.some((word) => title.includes(word))) score += 4;
      if (answers.character === 'expressive' && ['valencia', 'bordeaux', 'verona', 'rose', 'blue'].some((word) => title.includes(word))) score += 4;
      if (/ii\b/i.test(title)) score += 0.3;
      return score;
    };

    const showResult = () => {
      if (!(quizForm instanceof HTMLFormElement)) return;
      const answers = Object.fromEntries(new FormData(quizForm).entries());
      const available = variants.filter((variant) => variant.available);
      const pool = available.length ? available : variants;
      selectedVariant = [...pool].sort((a, b) => scoreVariant(b, answers) - scoreVariant(a, answers))[0] || null;
      if (!selectedVariant) {
        showStatus('No live Signature variants are available to recommend.', true);
        return;
      }

      steps.forEach((step) => step.classList.remove('is-active'));
      if (result instanceof HTMLElement) result.hidden = false;
      if (quizActions instanceof HTMLElement) quizActions.hidden = true;
      if (progress) progress.textContent = 'Your recommendation';
      if (progressBar instanceof HTMLElement) progressBar.style.width = '100%';

      const resultTitle = root.querySelector('[data-al-result-title]');
      const resultReason = root.querySelector('[data-al-result-reason]');
      const resultPrice = root.querySelector('[data-al-result-price]');
      const resultLink = root.querySelector('[data-al-result-link]');
      const resultMedia = root.querySelector('[data-al-result-media]');
      if (resultTitle) resultTitle.textContent = cleanTitle(selectedVariant.title);
      if (resultPrice) resultPrice.textContent = selectedVariant.price;
      if (resultReason) resultReason.textContent = 'Every current Signature colourway uses the same T700 performance platform. This match is based on the visual character you chose and live availability—not a made-up performance difference between colours.';
      if (resultLink instanceof HTMLAnchorElement) resultLink.href = selectedVariant.url;
      if (resultMedia instanceof HTMLElement) {
        resultMedia.replaceChildren();
        if (selectedVariant.image) {
          const image = document.createElement('img');
          image.src = selectedVariant.image;
          image.alt = `ALDREN Signature ${cleanTitle(selectedVariant.title)}`;
          resultMedia.append(image);
        }
      }
    };

    root.querySelectorAll('[data-al-open-quiz]').forEach((opener) => {
      opener.addEventListener('click', () => {
        if (!(dialog instanceof HTMLDialogElement)) return;
        if (!dialog.open) dialog.showModal();
        setStep(activeStep);
      });
    });

    root.querySelector('[data-al-close-quiz]')?.addEventListener('click', () => {
      if (dialog instanceof HTMLDialogElement) dialog.close();
    });

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog && dialog instanceof HTMLDialogElement) dialog.close();
    });

    nextButton?.addEventListener('click', () => {
      const current = steps[activeStep];
      const checked = current?.querySelector('input:checked');
      if (!checked) {
        showStatus('Choose one answer to continue.', true);
        current?.querySelector('input')?.focus();
        return;
      }
      if (activeStep === steps.length - 1) showResult();
      else setStep(activeStep + 1);
    });

    backButton?.addEventListener('click', () => setStep(activeStep - 1));

    root.querySelector('[data-al-retake]')?.addEventListener('click', () => {
      if (quizForm instanceof HTMLFormElement) quizForm.reset();
      if (result instanceof HTMLElement) result.hidden = true;
      if (quizActions instanceof HTMLElement) quizActions.hidden = false;
      selectedVariant = null;
      setStep(0);
    });

    root.querySelector('[data-al-result-add]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement) || !selectedVariant) return;
      button.disabled = true;
      const original = button.textContent;
      button.textContent = addingLabel;
      try {
        const cart = await addVariant(selectedVariant.id);
        button.textContent = 'Added';
        showStatus(`Added ${cleanTitle(selectedVariant.title)}. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart.`);
      } catch (error) {
        button.textContent = original;
        showStatus(error instanceof Error ? error.message : 'Could not add this colourway.', true);
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = original;
        }, 1800);
      }
    });
  });
})();
